import Service, { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { TrackedMap } from 'tracked-built-ins';

/**
 * Event Data Service
 *
 * Manages MQTT subscriptions and stores reference/timing data for multiple events.
 * Data is stored in tracked Maps for reactivity with Ember's rendering system.
 *
 * All data uses UUID-based keys and fields:
 * - Reference data maps are keyed by `uuid`
 * - Timing records are keyed by `entryUuid`
 * - Relationships use UUID fields (e.g., classUuid, classGroupUuid)
 */
export default class EventDataService extends Service {
  @service websocket;

  // Map of eventId -> event data structure
  @tracked events = new TrackedMap();

  // Version counter to force reactivity when data changes
  @tracked _dataVersion = 0;

  // Currently subscribed event IDs
  @tracked subscribedEvents = new Set();

  /**
   * Subscribe to an event's MQTT topics
   * Connects WebSocket and starts receiving data for the event
   * Returns a promise that resolves when WebSocket is connected
   */
  async subscribeToEvent(eventId) {
    if (this.subscribedEvents.has(eventId)) {
      console.log(`[EventData] Already subscribed to event ${eventId}`);
      return;
    }

    console.log(`[EventData] Subscribing to event ${eventId}`);

    // Initialize data structure for this event
    if (!this.events.has(eventId)) {
      this.events.set(eventId, this.createEventDataStructure(eventId));
    }

    // Connect WebSocket for this event and wait for connection
    await this.websocket.connect(eventId);
    this.subscribedEvents.add(eventId);

    // Set up message handler
    this.setupMessageHandler();
  }

  /**
   * Unsubscribe from an event's MQTT topics
   */
  unsubscribeFromEvent(eventId) {
    console.log(`[EventData] Unsubscribing from event ${eventId}`);

    this.subscribedEvents.delete(eventId);

    // If no more subscriptions, disconnect WebSocket
    if (this.subscribedEvents.size === 0) {
      this.websocket.disconnect();
    }
  }

  /**
   * Create initial data structure for an event
   * All maps use UUID-based keys for consistency
   */
  createEventDataStructure(eventId) {
    return {
      eventId,

      // Reference Data (keyed by uuid)
      event: null,
      classGroups: new TrackedMap(), // uuid -> classGroup
      classes: new TrackedMap(),      // uuid -> class
      entries: new TrackedMap(),      // uuid -> entry
      courses: new TrackedMap(),      // uuid -> course
      tieTimingRecords: new TrackedMap(), // entryUuid -> tieRecord
      penaltyTimingRecords: new TrackedMap(), // entryUuid -> array of penalties

      // Timing Data (keyed by entryUuid)
      startRecords: new TrackedMap(),      // entryUuid -> startRecord
      finishRecords: new TrackedMap(),     // entryUuid -> finishRecord
      intermediateRecords: new TrackedMap(), // entryUuid -> array of intermediate records

      // Metadata
      lastUpdated: null,
      snapshotComplete: false,
      snapshotProgress: {
        event: false,
        classgroups: false,
        classes: false,
        entries: false,
        courses: false,
        tietimingrecords: false,
        penaltytimingrecords: false,
        timing: false
      }
    };
  }

  /**
   * Set up message handler to route MQTT messages
   */
  setupMessageHandler() {
    // We'll process messages from websocket.messages
    // Since messages array is tracked, we can observe changes
    // For now, we'll poll in the component/route
    // TODO: Consider using a more reactive approach
  }

  /**
   * Process a single MQTT message
   */
  processMessage(message) {
    // Support both 'messageType' and 'type' fields (Lambda uses 'type' for timing updates)
    const msgType = message.messageType || message.type;
    if (!message || !msgType) {
      console.warn('[EventData] Invalid message (no messageType or type):', message);
      return;
    }

    // Messages now use eventUuid (not numeric eventId)
    // We store data under the UUID
    let eventId = message.eventUuid || message.eventId;
    if (!this.events.has(eventId)) {
      // Try to get UUID from nested data
      const dataEventUuid = message.data?.eventUuid;
      if (dataEventUuid && this.events.has(dataEventUuid)) {
        eventId = dataEventUuid;
      } else {
        console.warn('[EventData] Message for unsubscribed event:', eventId, 'from message:', message.eventUuid || message.eventId);
        return;
      }
    }

    console.log(`[EventData] Processing ${msgType} for event ${eventId}`);

    switch (msgType) {
      case 'reference_snapshot':
        this.handleReferenceSnapshot(eventId, message);
        break;
      case 'reference_update':
        this.handleReferenceUpdate(eventId, message);
        break;
      case 'timing_event':
      case 'timing_update': // Lambda broadcasts use 'timing_update'
        this.handleTimingEvent(eventId, message);
        break;
      case 'timing_events_batch':
      case 'timing_batch_update': // Lambda broadcasts use 'timing_batch_update'
        this.handleTimingEventsBatch(eventId, message);
        break;
      case 'snapshot_batch':
        this.handleTimingSnapshotBatch(eventId, message);
        break;
      default:
        console.log(`[EventData] Unknown message type: ${msgType}`);
    }

    // Update last updated timestamp and increment version to trigger reactivity
    const eventData = this.events.get(eventId);
    eventData.lastUpdated = new Date();
    this._dataVersion++;
  }

  /**
   * Handle reference data snapshot message
   * All data is keyed by uuid (for reference data) or entryUuid (for timing-related records)
   */
  handleReferenceSnapshot(eventId, message) {
    const eventData = this.events.get(eventId);
    const { dataType, data } = message;

    console.log(`[EventData] Reference snapshot: ${dataType}, count: ${Array.isArray(data) ? data.length : 1}`);

    switch (dataType) {
      case 'event':
        eventData.event = data;
        eventData.snapshotProgress.event = true;
        break;

      case 'classgroups':
        data.forEach(cg => eventData.classGroups.set(cg.uuid, cg));
        eventData.snapshotProgress.classgroups = true;
        break;

      case 'classes':
        data.forEach(cls => eventData.classes.set(cls.uuid, cls));
        eventData.snapshotProgress.classes = true;
        break;

      case 'entries':
        // Entries come in batches, keyed by uuid
        data.forEach(entry => eventData.entries.set(entry.uuid, entry));

        // Mark complete only when we receive the last batch
        if (message.batchIndex === message.totalBatches) {
          eventData.snapshotProgress.entries = true;
        }
        break;

      case 'courses':
        data.forEach(course => eventData.courses.set(course.uuid, course));
        eventData.snapshotProgress.courses = true;
        break;

      case 'tietimingrecords':
        // Keyed by entryUuid
        data.forEach(tie => eventData.tieTimingRecords.set(tie.entryUuid, tie));
        eventData.snapshotProgress.tietimingrecords = true;
        break;

      case 'penaltytimingrecords':
        // Group penalties by entryUuid
        data.forEach(penalty => {
          const entryUuid = penalty.entryUuid;
          const existing = eventData.penaltyTimingRecords.get(entryUuid) || [];
          // Create new array for reactivity
          eventData.penaltyTimingRecords.set(entryUuid, [...existing, penalty]);
        });
        eventData.snapshotProgress.penaltytimingrecords = true;
        break;

      default:
        console.warn(`[EventData] Unknown snapshot dataType: ${dataType}`);
    }

    // Check if all snapshots are complete
    this.checkSnapshotCompletion(eventId);
  }

  /**
   * Handle reference data update message
   * Message format: { messageType: 'reference_update', changes: { entries: {...}, classGroups: {...}, ... } }
   * Each data type in changes has: { added: [], updated: [], deleted: [] }
   * All items use uuid as key
   */
  handleReferenceUpdate(eventId, message) {
    const eventData = this.events.get(eventId);
    const { changes } = message;

    console.log(`[EventData] Reference update received:`, changes);

    // Process each data type in the changes object
    for (const [dataType, change] of Object.entries(changes)) {
      if (!change) continue;

      console.log(`[EventData] Processing ${dataType}:`, {
        added: change.added?.length || 0,
        updated: change.updated?.length || 0,
        deleted: change.deleted?.length || 0
      });

      switch (dataType) {
        case 'event':
          if (change.updated) {
            eventData.event = change.updated;
          }
          break;

        case 'classGroups':
          this.applyArrayChangesByUuid(eventData.classGroups, change);
          break;

        case 'classes':
          this.applyArrayChangesByUuid(eventData.classes, change);
          break;

        case 'entries':
          this.applyArrayChangesByUuid(eventData.entries, change);
          break;

        case 'courses':
          this.applyArrayChangesByUuid(eventData.courses, change);
          break;

        case 'tieTimingRecords':
          // Keyed by entryUuid
          if (change.added) change.added.forEach(item => eventData.tieTimingRecords.set(item.entryUuid, item));
          if (change.updated) change.updated.forEach(item => eventData.tieTimingRecords.set(item.entryUuid, item));
          if (change.deleted) change.deleted.forEach(uuid => {
            // deleted contains uuid, need to find by iterating or just try to delete
            for (const [key, record] of eventData.tieTimingRecords.entries()) {
              if (record.uuid === uuid) {
                eventData.tieTimingRecords.delete(key);
                break;
              }
            }
          });
          break;

        case 'penaltyTimingRecords':
          // For added penalties, append to the entry's penalty array (keyed by entryUuid)
          if (change.added) {
            change.added.forEach(penalty => {
              const entryUuid = penalty.entryUuid;
              const existing = eventData.penaltyTimingRecords.get(entryUuid) || [];
              // Create new array for reactivity
              eventData.penaltyTimingRecords.set(entryUuid, [...existing, penalty]);
            });
          }
          // For updated penalties, find and replace by uuid
          if (change.updated) {
            change.updated.forEach(penalty => {
              const entryUuid = penalty.entryUuid;
              const penalties = eventData.penaltyTimingRecords.get(entryUuid) || [];
              const idx = penalties.findIndex(p => p.uuid === penalty.uuid);
              let newPenalties;
              if (idx >= 0) {
                // Create new array for reactivity
                newPenalties = [...penalties];
                newPenalties[idx] = penalty;
              } else {
                newPenalties = [...penalties, penalty];
              }
              eventData.penaltyTimingRecords.set(entryUuid, newPenalties);
            });
          }
          // For deleted penalties, remove by uuid
          if (change.deleted) {
            change.deleted.forEach(penaltyUuid => {
              // Need to find which entryUuid this penalty belongs to
              for (const [entryUuid, penalties] of eventData.penaltyTimingRecords.entries()) {
                const filtered = penalties.filter(p => p.uuid !== penaltyUuid);
                if (filtered.length !== penalties.length) {
                  eventData.penaltyTimingRecords.set(entryUuid, filtered);
                  break;
                }
              }
            });
          }
          break;

        default:
          console.warn(`[EventData] Unknown reference update dataType: ${dataType}`);
      }
    }
  }

  /**
   * Apply array changes (added, updated, deleted) to a Map using uuid as key
   */
  applyArrayChangesByUuid(map, change) {
    if (change.added) {
      change.added.forEach(item => map.set(item.uuid, item));
    }
    if (change.updated) {
      change.updated.forEach(item => map.set(item.uuid, item));
    }
    if (change.deleted) {
      // deleted contains uuids
      change.deleted.forEach(uuid => map.delete(uuid));
    }
  }

  /**
   * Handle single timing event
   * Timing records are keyed by entryUuid (not numeric EntryId)
   */
  handleTimingEvent(eventId, message) {
    const eventData = this.events.get(eventId);
    const { recordType, operation, data } = message;

    // Use entryUuid (UUID-based format)
    const entryUuid = data?.entryUuid || null;
    const recordUuid = data?.uuid;

    console.log(`[EventData] Timing event: ${operation} ${recordType}`, {
      entryUuid,
      recordUuid,
      hasFinishTime: !!data?.finishTime,
      hasStartTime: !!data?.startTime,
      data: data
    });

    // Note: entryUuid can be null for unassociated records - don't return early

    switch (recordType) {
      case 'start': {
        const removeExistingByUUID = () => {
          if (!recordUuid) return null;
          for (const [key, record] of eventData.startRecords.entries()) {
            if (record?.uuid === recordUuid) {
              eventData.startRecords.delete(key);
              console.log(`[EventData] Removed start record from entry ${key} (uuid: ${recordUuid})`);
              return key;
            }
          }
          return null;
        };

        if (operation === 'DELETE') {
          removeExistingByUUID();
        } else if (operation === 'INSERT') {
          if (entryUuid) {
            eventData.startRecords.set(entryUuid, data);
            console.log(`[EventData] Inserted start record for entry ${entryUuid}`);
          }
        } else {
          // UPDATE
          removeExistingByUUID();
          if (entryUuid) {
            eventData.startRecords.set(entryUuid, data);
            console.log(`[EventData] Updated start record for entry ${entryUuid}`);
          }
        }
        break;
      }

      case 'finish': {
        const removeExistingByUUID = () => {
          if (!recordUuid) return null;
          for (const [key, record] of eventData.finishRecords.entries()) {
            if (record?.uuid === recordUuid) {
              eventData.finishRecords.delete(key);
              console.log(`[EventData] Removed finish record from entry ${key} (uuid: ${recordUuid})`);
              return key;
            }
          }
          return null;
        };

        if (operation === 'DELETE') {
          removeExistingByUUID();
        } else if (operation === 'INSERT') {
          if (entryUuid) {
            eventData.finishRecords.set(entryUuid, data);
            console.log(`[EventData] Inserted finish record for entry ${entryUuid}`);
          }
        } else {
          // UPDATE: Remove old record, then add new data at new entryUuid (if not null)
          removeExistingByUUID();
          if (entryUuid) {
            eventData.finishRecords.set(entryUuid, data);
            console.log(`[EventData] Updated finish record for entry ${entryUuid}`);
          }
        }
        break;
      }

      case 'intermediate': {
        // Helper to find and remove intermediate record by UUID from ANY entry's array
        const removeExistingByUUID = () => {
          if (!recordUuid) return null;
          for (const [key, records] of eventData.intermediateRecords.entries()) {
            const existingIndex = records.findIndex(r => r.uuid === recordUuid);
            if (existingIndex >= 0) {
              const newRecords = records.filter(r => r.uuid !== recordUuid);
              if (newRecords.length > 0) {
                eventData.intermediateRecords.set(key, newRecords);
              } else {
                eventData.intermediateRecords.delete(key);
              }
              console.log(`[EventData] Removed intermediate record from entry ${key} (uuid: ${recordUuid})`);
              return key;
            }
          }
          return null;
        };

        if (operation === 'DELETE') {
          removeExistingByUUID();
        } else if (operation === 'INSERT') {
          if (entryUuid) {
            const existingRecords = eventData.intermediateRecords.get(entryUuid) || [];
            eventData.intermediateRecords.set(entryUuid, [...existingRecords, data]);
            console.log(`[EventData] Inserted intermediate record for entry ${entryUuid}`);
          }
        } else {
          // UPDATE: Remove old record from wherever it is, then add at new entryUuid
          removeExistingByUUID();
          if (entryUuid) {
            const existingRecords = eventData.intermediateRecords.get(entryUuid) || [];
            eventData.intermediateRecords.set(entryUuid, [...existingRecords, data]);
            console.log(`[EventData] Updated intermediate record for entry ${entryUuid}`);
          }
        }
        break;
      }
    }
  }

  /**
   * Handle batch of timing events
   */
  handleTimingEventsBatch(eventId, message) {
    const { events } = message;
    console.log(`[EventData] Processing timing events batch: ${events.length} events`);

    events.forEach(event => {
      this.handleTimingEvent(eventId, event);
    });
  }

  /**
   * Handle timing snapshot batch
   * Records are keyed by entryUuid
   */
  handleTimingSnapshotBatch(eventId, message) {
    const eventData = this.events.get(eventId);
    const { recordType, records, isLastBatch } = message;

    console.log(`[EventData] Timing snapshot batch: ${recordType}, count: ${records.length}, last: ${isLastBatch}`);

    switch (recordType) {
      case 'start':
        records.forEach(record => {
          if (record.entryUuid) {
            eventData.startRecords.set(record.entryUuid, record);
          }
        });
        break;

      case 'finish':
        records.forEach(record => {
          if (record.entryUuid) {
            eventData.finishRecords.set(record.entryUuid, record);
          }
        });
        break;

      case 'intermediate':
        records.forEach(record => {
          if (record.entryUuid) {
            const existing = eventData.intermediateRecords.get(record.entryUuid) || [];
            // Create new array for reactivity
            eventData.intermediateRecords.set(record.entryUuid, [...existing, record]);
          }
        });
        break;
    }

    if (isLastBatch) {
      eventData.snapshotProgress.timing = true;
      this.checkSnapshotCompletion(eventId);
    }
  }

  /**
   * Check if all snapshots are complete
   */
  checkSnapshotCompletion(eventId) {
    const eventData = this.events.get(eventId);
    const progress = eventData.snapshotProgress;

    const allComplete = progress.event &&
                        progress.classgroups &&
                        progress.classes &&
                        progress.entries &&
                        progress.courses &&
                        progress.tietimingrecords &&
                        progress.penaltytimingrecords &&
                        progress.timing;

    console.log(`[EventData] checkSnapshotCompletion for event ${eventId}:`, progress, `allComplete: ${allComplete}`);

    if (allComplete && !eventData.snapshotComplete) {
      eventData.snapshotComplete = true;
      console.log(`[EventData] Snapshot complete for event ${eventId}`);
      // Re-set in TrackedMap to trigger reactivity
      this.events.set(eventId, eventData);
    }
  }

  /**
   * Get all data for an event
   */
  getEventData(eventId) {
    return this.events.get(eventId);
  }

  /**
   * Get classgroups for an event
   */
  getClassGroups(eventId) {
    const eventData = this.events.get(eventId);
    if (!eventData) return [];

    return Array.from(eventData.classGroups.values());
  }

  /**
   * Get entries for a specific classgroup
   * @param {string} eventId - The event ID
   * @param {string} classGroupUuid - The classgroup UUID
   */
  getEntriesForClassGroup(eventId, classGroupUuid) {
    const eventData = this.events.get(eventId);
    if (!eventData) {
      console.warn(`[EventData] getEntriesForClassGroup: no event data for ${eventId}`);
      return [];
    }

    // Get all classes for this classgroup (using classGroupUuid)
    const allClasses = Array.from(eventData.classes.values());
    const classUuidsInGroup = allClasses
      .filter(cls => cls.classGroupUuid === classGroupUuid)
      .map(cls => cls.uuid);

    console.log(`[EventData] getEntriesForClassGroup(${eventId}, ${classGroupUuid}):`, {
      totalClasses: allClasses.length,
      classesInGroup: classUuidsInGroup.length,
      totalEntries: eventData.entries.size,
      sampleClass: allClasses[0],
      sampleEntry: Array.from(eventData.entries.values())[0]
    });

    // Get all entries for these classes (using classUuid)
    const entries = Array.from(eventData.entries.values())
      .filter(entry => classUuidsInGroup.includes(entry.classUuid));

    console.log(`[EventData] Found ${entries.length} entries for classgroup ${classGroupUuid}`);

    return entries;
  }

  /**
   * Get course for a classgroup
   * @param {string} eventId - The event ID
   * @param {string} classGroupUuid - The classgroup UUID
   */
  getCourseForClassGroup(eventId, classGroupUuid) {
    const eventData = this.events.get(eventId);
    if (!eventData) return null;

    const classGroup = eventData.classGroups.get(classGroupUuid);
    if (!classGroup || !classGroup.courseUuid) return null;

    return eventData.courses.get(classGroup.courseUuid);
  }

  /**
   * Load initial reference data from REST API response
   * All data uses uuid as keys
   */
  loadReferenceData(eventId, referenceData) {
    const eventDataObj = this.events.get(eventId);
    if (!eventDataObj) {
      console.warn(`[EventData] Cannot load reference data: event ${eventId} not initialized`);
      return;
    }

    console.log(`[EventData] Loading initial reference data for event ${eventId}`);

    // Clear existing reference data first (important for page refresh to get fresh state)
    eventDataObj.classGroups.clear();
    eventDataObj.classes.clear();
    eventDataObj.entries.clear();
    eventDataObj.courses.clear();
    eventDataObj.tieTimingRecords.clear();
    eventDataObj.penaltyTimingRecords.clear();

    // Load event
    if (referenceData.event) {
      eventDataObj.event = referenceData.event;
    }
    eventDataObj.snapshotProgress.event = true;

    // Load classGroups (keyed by uuid)
    if (referenceData.classGroups && referenceData.classGroups.length > 0) {
      referenceData.classGroups.forEach(cg => eventDataObj.classGroups.set(cg.uuid, cg));
    }
    eventDataObj.snapshotProgress.classgroups = true;

    // Load classes (keyed by uuid)
    if (referenceData.classes && referenceData.classes.length > 0) {
      referenceData.classes.forEach(cls => eventDataObj.classes.set(cls.uuid, cls));
    }
    eventDataObj.snapshotProgress.classes = true;

    // Load entries (keyed by uuid)
    if (referenceData.entries && referenceData.entries.length > 0) {
      referenceData.entries.forEach(entry => eventDataObj.entries.set(entry.uuid, entry));
    }
    eventDataObj.snapshotProgress.entries = true;

    // Load courses (keyed by uuid)
    if (referenceData.courses && referenceData.courses.length > 0) {
      referenceData.courses.forEach(course => eventDataObj.courses.set(course.uuid, course));
    }
    eventDataObj.snapshotProgress.courses = true;

    // Load tieTimingRecords (keyed by entryUuid)
    if (referenceData.tieTimingRecords && referenceData.tieTimingRecords.length > 0) {
      referenceData.tieTimingRecords.forEach(tie => eventDataObj.tieTimingRecords.set(tie.entryUuid, tie));
    }
    eventDataObj.snapshotProgress.tietimingrecords = true;

    // Load penaltyTimingRecords (keyed by entryUuid)
    if (referenceData.penaltyTimingRecords && referenceData.penaltyTimingRecords.length > 0) {
      referenceData.penaltyTimingRecords.forEach(penalty => {
        const entryUuid = penalty.entryUuid;
        const existing = eventDataObj.penaltyTimingRecords.get(entryUuid) || [];
        // Create new array for reactivity
        eventDataObj.penaltyTimingRecords.set(entryUuid, [...existing, penalty]);
      });
    }
    eventDataObj.snapshotProgress.penaltytimingrecords = true;

    console.log(`[EventData] Reference data loaded:`, {
      classGroups: eventDataObj.classGroups.size,
      classes: eventDataObj.classes.size,
      entries: eventDataObj.entries.size,
      courses: eventDataObj.courses.size
    });
  }

  /**
   * Load initial timing data from REST API response
   * Timing records are keyed by entryUuid
   */
  loadTimingData(eventId, timingData) {
    const eventDataObj = this.events.get(eventId);
    if (!eventDataObj) {
      console.warn(`[EventData] Cannot load timing data: event ${eventId} not initialized`);
      return;
    }

    console.log(`[EventData] Loading initial timing data for event ${eventId}`);

    // Clear existing timing data first (important for page refresh to get fresh state)
    eventDataObj.startRecords.clear();
    eventDataObj.finishRecords.clear();
    eventDataObj.intermediateRecords.clear();

    // Load startRecords (keyed by entryUuid)
    if (timingData.startRecords && timingData.startRecords.length > 0) {
      timingData.startRecords.forEach(record => {
        if (record.entryUuid) {
          eventDataObj.startRecords.set(record.entryUuid, record);
        }
      });
    }

    // Load finishRecords (keyed by entryUuid)
    // Note: Unassigned records (entryUuid: null) are filtered out at the sync source
    // and should not appear here. If they do, skip them silently.
    if (timingData.finishRecords && timingData.finishRecords.length > 0) {
      let skipped = 0;
      timingData.finishRecords.forEach(record => {
        if (record.entryUuid) {
          eventDataObj.finishRecords.set(record.entryUuid, record);
        } else {
          skipped++;
        }
      });
      if (skipped > 0) {
        console.log(`[EventData] Loaded ${timingData.finishRecords.length - skipped} finish records, skipped ${skipped} unassigned`);
      }
    }

    // Load intermediateRecords (keyed by entryUuid)
    // Note: Unassigned records (entryUuid: null) are filtered out at the sync source
    if (timingData.intermediateRecords && timingData.intermediateRecords.length > 0) {
      let skipped = 0;
      timingData.intermediateRecords.forEach(record => {
        if (record.entryUuid) {
          const existing = eventDataObj.intermediateRecords.get(record.entryUuid) || [];
          // Create new array for reactivity
          eventDataObj.intermediateRecords.set(record.entryUuid, [...existing, record]);
        } else {
          skipped++;
        }
      });
      if (skipped > 0) {
        console.log(`[EventData] Loaded ${timingData.intermediateRecords.length - skipped} intermediate records, skipped ${skipped} unassigned`);
      }
    }

    eventDataObj.snapshotProgress.timing = true;

    console.log(`[EventData] Timing data loaded:`, {
      starts: eventDataObj.startRecords.size,
      finishes: eventDataObj.finishRecords.size,
      intermediates: eventDataObj.intermediateRecords.size
    });

    // Check if all data is now complete
    this.checkSnapshotCompletion(eventId);
  }

  willDestroy() {
    super.willDestroy(...arguments);

    // Unsubscribe from all events
    this.subscribedEvents.forEach(eventId => {
      this.unsubscribeFromEvent(eventId);
    });
  }
}
